import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface WeatherData {
  temp: number;
  condition: string;
  humidity: number;
  windSpeed: number;
  feelsLike: number;
  icon: string;
  location: string;
  country: string;
  description: string;
  sunrise: string;
  sunset: string;
  uv: number;
}

const DEFAULT_WEATHER: WeatherData = {
  temp: 28,
  condition: 'Partly Cloudy',
  humidity: 70,
  windSpeed: 12,
  feelsLike: 32,
  icon: 'cloud-sun',
  location: 'Mumbai',
  country: 'IN',
  description: 'Weather data loading...',
  sunrise: '',
  sunset: '',
  uv: 6,
};

export function useWeather() {
  const [weather, setWeather] = useState<WeatherData>(DEFAULT_WEATHER);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWeather = useCallback(async (latitude?: number, longitude?: number, city?: string) => {
    setLoading(true);
    setError(null);

    try {
      // Get current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        // User not authenticated - use default weather
        setWeather(DEFAULT_WEATHER);
        setLoading(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('get-weather', {
        body: { latitude, longitude, city },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (fnError) {
        throw fnError;
      }

      if (data) {
        setWeather(data);
        
        // Update weather cache for proactive notifications
        try {
          await supabase.from('weather_cache').upsert({
            user_id: session.user.id,
            last_temp: data.temp,
            last_condition: data.condition,
            last_location: data.location,
            last_checked_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id' });
        } catch (cacheError) {
          console.log('Weather cache update skipped');
        }
      }
    } catch (err) {
      console.error('Weather fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch weather');
      // Keep default weather on error
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWeatherByLocation = useCallback(async () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          fetchWeather(position.coords.latitude, position.coords.longitude);
        },
        (err) => {
          console.log('Geolocation denied, using default location');
          fetchWeather(undefined, undefined, 'Mumbai');
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    } else {
      fetchWeather(undefined, undefined, 'Mumbai');
    }
  }, [fetchWeather]);

  useEffect(() => {
    fetchWeatherByLocation();
    
    // Refresh weather every 30 minutes
    const interval = setInterval(fetchWeatherByLocation, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeatherByLocation]);

  return {
    weather,
    loading,
    error,
    refetch: fetchWeatherByLocation,
    fetchByCity: (city: string) => fetchWeather(undefined, undefined, city),
  };
}

// Get outfit recommendations based on weather
export function getWeatherOutfitTips(weather: WeatherData): string[] {
  const tips: string[] = [];
  
  if (weather.temp > 35) {
    tips.push('Light, breathable fabrics recommended');
    tips.push('Consider linen or cotton');
    tips.push('Light colors to reflect heat');
  } else if (weather.temp > 28) {
    tips.push('Light layers work well');
    tips.push('Breathable materials recommended');
  } else if (weather.temp > 20) {
    tips.push('Perfect layering weather');
    tips.push('Light jacket might be useful');
  } else if (weather.temp > 10) {
    tips.push('Warm layers recommended');
    tips.push('Consider a jacket or sweater');
  } else {
    tips.push('Heavy outerwear recommended');
    tips.push('Layer up for warmth');
  }
  
  if (weather.condition.includes('Rain') || weather.condition.includes('Drizzle') || weather.condition.includes('Shower')) {
    tips.push('Waterproof outerwear essential');
    tips.push('Avoid light-colored shoes');
  }
  
  if (weather.humidity > 80) {
    tips.push('Choose moisture-wicking fabrics');
    tips.push('Avoid heavy layering');
  }
  
  if (weather.uv > 6) {
    tips.push('Consider sun protection');
    tips.push('Hat or sunglasses recommended');
  }
  
  return tips.slice(0, 3);
}
